<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useLiveStore } from '@/stores/live';
import { useFleetStore } from '@/stores/fleet';
import StatusDot from './StatusDot.vue';
import { clockTime, shortDate, dateTime } from '@/utils/format';

const auth = useAuthStore();
const live = useLiveStore();
const fleet = useFleetStore();
const route = useRoute();
const router = useRouter();

const now = ref(new Date());
let timer: number | undefined;

onMounted(() => {
  timer = window.setInterval(() => { now.value = new Date(); }, 1000);
  void fleet.load();
});
onBeforeUnmount(() => { if (timer) clearInterval(timer); });

/** The site section keeps its own tabs; this is the top-level navigation. */
const siteId = computed(() => route.params.id as string | undefined);

const nav = computed(() => {
  const items = [
    { name: 'Network Overview Dashboard', to: { name: 'overview' } as const },
  ];
  if (siteId.value) {
    items.push(
      { name: 'Receive Chain', to: { name: 'site-chain', params: { id: siteId.value } } as never },
      { name: 'Spectrum', to: { name: 'site-spectrum', params: { id: siteId.value } } as never },
      { name: 'Trends', to: { name: 'site-trends', params: { id: siteId.value } } as never },
    );
  }
  items.push({ name: 'Alarms', to: { name: 'alarms' } as never });
  if (auth.isOperator) items.push({ name: 'Settings', to: { name: 'settings' } as never });
  return items;
});

const activeAlarms = computed(() =>
  fleet.sites.reduce((total, s) => total + s.activeAlarms, 0),
);

/** Newest telemetry timestamp across the fleet. */
const lastUpdate = computed<string | null>(() => {
  let newest: string | null = null;
  for (const s of fleet.sites) {
    if (s.lastMessageAt && (!newest || s.lastMessageAt > newest)) newest = s.lastMessageAt;
  }
  return newest;
});

/**
 * Nothing anywhere in the fleet for two minutes means the screen is no longer
 * live, whatever the clock says. `now` is in the dependency list so this
 * re-evaluates every second rather than only when telemetry arrives.
 */
const updateIsStale = computed(() => {
  if (!lastUpdate.value) return true;
  return now.value.getTime() - new Date(lastUpdate.value).getTime() > 120_000;
});

async function signOut(): Promise<void> {
  await auth.logout();
  await router.push({ name: 'login' });
}
</script>

<template>
  <header class="hdr">
    <RouterLink :to="{ name: 'overview' }" class="hdr__brand">
      <svg viewBox="0 0 32 32" class="hdr__mark" aria-hidden="true">
        <path d="M4 24 L16 6 L28 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="16" cy="25" r="2.5" fill="currentColor"/>
        <path d="M9 17a9 9 0 0 1 14 0" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.55"/>
      </svg>
      <span class="hdr__brandtext">
        <strong>RF-SDAS</strong>
        <small>RF Sensing Detection Analytics System</small>
      </span>
    </RouterLink>

    <nav class="hdr__nav" aria-label="Primary">
      <RouterLink
        v-for="item in nav"
        :key="item.name"
        :to="item.to"
        class="hdr__link"
        active-class="hdr__link--active"
      >
        {{ item.name }}
        <span
          v-if="item.name === 'Alarms' && activeAlarms > 0"
          class="hdr__badge"
        >{{ activeAlarms }}</span>
      </RouterLink>
    </nav>

    <div class="hdr__right">
      <StatusDot
        :state="live.connected ? 'online' : 'offline'"
        :pulse="live.connected"
      />
      <span class="hdr__clock">
        <strong>{{ clockTime(now) }}</strong>
        <small>{{ shortDate(now) }} SGT</small>
      </span>

      <!--
        Wall-clock time alone cannot tell an operator whether the screen is
        current. This is the newest telemetry timestamp anywhere in the fleet,
        so a frozen feed is visible even when the clock keeps ticking.
      -->
      <span class="hdr__updated" :class="{ 'hdr__updated--stale': updateIsStale }">
        <small>Last Update</small>
        <strong>{{ lastUpdate ? dateTime(lastUpdate) : 'no data yet' }}</strong>
      </span>

      <div class="hdr__user">
        <span class="hdr__name">{{ auth.user?.name }}</span>
        <span class="hdr__role" :class="`hdr__role--${auth.user?.role}`">
          {{ auth.user?.role }}
        </span>
      </div>

      <button class="btn btn--ghost hdr__out" type="button" @click="signOut">
        Sign out
      </button>
    </div>
  </header>
</template>

<style scoped>
.hdr {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px var(--gap);
  background: linear-gradient(180deg, #0d1b31, var(--bg-panel));
  border-bottom: 1px solid var(--line);
  box-shadow: 0 1px 0 #29c6f026, 0 6px 22px #00000055;
  position: sticky;
  top: 0;
  z-index: 20;
  flex-wrap: wrap;
}

/* Accent rule under the header, brightest at the brand end. */
.hdr::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 1px;
  background: linear-gradient(90deg, var(--accent), #29c6f000 55%);
  opacity: 0.7;
}

.hdr__brand { display: flex; align-items: center; gap: 10px; color: var(--accent); }
.hdr__mark { width: 30px; height: 30px; }

.hdr__brandtext { display: flex; flex-direction: column; line-height: 1.15; }
.hdr__brandtext strong {
  font-family: var(--font-display);
  font-size: 17px;
  letter-spacing: 0.08em;
  color: var(--text);
}
.hdr__brandtext small { font-size: 11px; color: var(--text-dim); letter-spacing: 0.04em; }

.hdr__nav { display: flex; gap: 2px; flex-wrap: wrap; }

.hdr__link {
  position: relative;
  padding: 8px 13px;
  border-radius: var(--radius);
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.hdr__link:hover { background: var(--bg-raised); color: var(--text); }
.hdr__link--active { background: var(--accent-soft); color: var(--accent-strong); }

.hdr__badge {
  display: inline-block;
  margin-left: 6px;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--crit);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.hdr__right { margin-left: auto; display: flex; align-items: center; gap: 14px; }

.hdr__clock { display: flex; flex-direction: column; line-height: 1.15; text-align: right; }
.hdr__clock strong { font-family: var(--font-mono); font-size: 15px; }
.hdr__clock small { font-size: 11px; color: var(--text-dim); }

.hdr__updated {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  text-align: right;
  padding-left: 14px;
  border-left: 1px solid var(--line);
}
.hdr__updated small {
  font-size: 9px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--text-dim);
}
.hdr__updated strong {
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-weight: 500;
  color: var(--text-muted);
  white-space: nowrap;
}
/* A stale feed is a fault condition, so it takes the warning colour. */
.hdr__updated--stale strong { color: var(--warn); }
.hdr__updated--stale small { color: var(--warn); }

.hdr__user { display: flex; flex-direction: column; line-height: 1.2; text-align: right; }
.hdr__name { font-size: 13px; font-weight: 500; }
.hdr__role {
  font-size: 10px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  font-weight: 700;
}
.hdr__role--operator { color: var(--ok); }
.hdr__role--viewer { color: var(--text-dim); }

@media (max-width: 900px) {
  .hdr__clock, .hdr__updated, .hdr__out { display: none; }
}
</style>
